import { useRef } from 'react'

import { postTicketTyping } from '@/services/support'

// The service caps typing beacons at 40/min, so one every three seconds is safe.
const BEACON_INTERVAL_MS = 3_000

interface TypingBeacon {
  notifyTyping: () => void
  resetBeacon: () => void
}

export interface LastBeacon {
  ticketPublicId: string
  sentAt: number
}

export const NO_BEACON_SENT: LastBeacon = { ticketPublicId: '', sentAt: 0 }

// Each ticket gets its own window: switching tickets must beacon straight away
// rather than wait out the window the previous thread opened.
export const isBeaconThrottled = (
  lastBeacon: LastBeacon,
  ticketPublicId: string,
  now: number,
) =>
  lastBeacon.ticketPublicId === ticketPublicId &&
  now - lastBeacon.sentAt < BEACON_INTERVAL_MS

// Throttled beacons that tell staff the user is writing. Switching tickets or
// sending a message starts a fresh window, so the next keystroke beacons again.
export const useTypingBeacon = (ticketPublicId: string): TypingBeacon => {
  const lastBeacon = useRef(NO_BEACON_SENT)

  return {
    notifyTyping: () => {
      const now = Date.now()
      if (isBeaconThrottled(lastBeacon.current, ticketPublicId, now)) return

      lastBeacon.current = { ticketPublicId, sentAt: now }
      // A beacon nobody receives is not worth telling the user about, rate
      // limits included. It is never retried either.
      postTicketTyping(ticketPublicId).catch(() => {})
    },

    resetBeacon: () => {
      lastBeacon.current = NO_BEACON_SENT
    },
  }
}
